<template>
  <div class="w-tw">
    <AppAlert
      v-if="isDownloading"
      type="info"
      message="Your export is being prepared and will start downloading shortly."
      class="mb-3 flex items-center rounded border border-blue-700 bg-blue-50 p-4 text-blue-700"
    />
  </div>
</template>

<script>
/**
 * WebSocket-based export downloading. Handles background exports via
 * BackgroundTaskNotificationsChannel with progressive user feedback.
 */
import { initBackgroundTaskNotifications } from '../channels/background_task_notifications_channel';

export default {
  name: 'AppExporterWebsockets',
  emits: ['complete'],
  data() {
    return {
      isDownloading: false,
      isQueued: false,
      downloadTimer: null,
      timers: [],
      subscription: null
    }
  },

  beforeUnmount() {
    if (this.downloadTimer) {
      clearTimeout(this.downloadTimer);
    }

    this.cleanUp();
  },

  methods: {
    download(format, { background = false, url = document.location } = {}) {
      const downloadUrl = new URL(url);

      this.isQueued = true;

      downloadUrl.searchParams.set('format', format);
      if (background) {
        downloadUrl.searchParams.set('background', 'true');
      }

      if (!background) {
        // Start a timer to show the alert after 2 seconds
        this.timers = [...this.timers, setTimeout(() => {
          this.cleanup();

          this.appMessage('Your export is being prepared, download will start shortly.', { life: 7000 });
        }, 2000)];
      }

      const makeRequest = (url, isRetry = false) => {
        this.cleanUp();
        this.timers = [...this.timers, setTimeout(() => {
          this.appMessage('Your export is being prepared, download will start shortly.', { life: 7000 });
        }, 2000)];

        this.timers = [...this.timers, setTimeout(() => {
          this.appMessage('This is taking a while. We\'ll email you the file if you leave this page.', { sticky: true })
        }, 10000)];

        return fetch(url)
          .then(response => {
            if (response.status === 500 && !background && !isRetry) {
              downloadUrl.searchParams.set('background', 'true');

              return makeRequest(downloadUrl, true);
            }

            if (response.status === 429) {
              // Rate limited - surface the server's alert instead of the "being prepared" message.
              this.cleanUp();
              this.$emit('complete');

              return response.json().then(({ alert }) => this.appMessage(alert, { sticky: true }));
            }

            const contentType = response.headers.get('content-type');

            if (contentType?.includes('application/json')) {
              // Background job, UI feedback will be handled by websockets
              this.downloadBackgroundResponse(response);

              return;
            }

            this.cleanUp();
            this.$emit('complete');

            return this.downloadResponse(response, { format });
          })
          .catch(() => {
            this.cleanUp();
            this.appMessage('Unable to generate export file for download', { sticky: true });
            this.$emit('complete');
          });
      };

      makeRequest(downloadUrl).finally(() => {
        if (!background) {
          clearTimeout(this.downloadTimer);
          this.isDownloading = false;
        }
      });
    },

    cleanUp() {
      this.timers.forEach(timer => clearTimeout(timer));
      this.timers = [];
      this.isQueued = false;

      if (this.subscription) {
        this.subscription.unsubscribe();
        this.subscription = null;
      }
    },

    async downloadBackgroundResponse(response) {
      const {
        success, action,
        organization_id: organizationId, user_id: userId
      } = await response.json();

      if (success) {
        const { subscription } = initBackgroundTaskNotifications(organizationId, userId, action, () => {
          this.cleanUp();

          this.$clearAppMessage();
          this.appMessage('Export completed successfully');
          this.$emit('complete');
        }, true);

        this.subscription = subscription;
      }
    },

    appMessage(message) {
      if (!this.hideToast) {
        this.$appMessage(message);
      }
    },

    async downloadResponse(response, { format }) {
      const type = format === 'csv' ? 'text/csv' : 'application/pdf';
      const blob = new Blob([await response.blob()], { type });
      const object = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const parts = response.headers.get('content-disposition').split(';');

      link.href = object;
      link.download = parts[1].split('=')[1].split('"').join('');
      link.click();

      setTimeout(() => URL.revokeObjectURL(link.href), 8000);
    }
  }
};
</script>
